import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Boton from '../Boton'
import { avisosApi } from '../../api/avisos'
import { fmtFechaHora } from '../../utils/fecha'
import { colors, text, radius } from '../../design/tokens'

/**
 * Ventana EMERGENTE de avisos — el canal garantizado (decisión de Martin:
 * no todos usan Telegram, pero todos miran LGR muchas veces al día).
 *
 * Aparece sobre cualquier pantalla cuando hay avisos sin leer y no se puede
 * descartar sin tocar "✓ Leído" (no tiene ✕ ni cierra con Escape — es un
 * acuse de recibo, no un popup decorativo). Si hay varios, se aceptan de a
 * uno. "Leído" NO es "resuelto": el aviso queda en la campanita hasta que el
 * destinatario lo marque hecho.
 */
export default function AvisoEmergente({ avisos, onLeido }) {
  const navigate = useNavigate()
  const [marcando, setMarcando] = useState(false)
  const aviso = avisos[0]
  if (!aviso) return null

  const aceptar = async (irAlDocumento = false) => {
    setMarcando(true)
    try {
      await avisosApi.marcarLeido(aviso.id)
      onLeido(aviso.id)
      if (irAlDocumento && aviso.referencia?.ruta) navigate(aviso.referencia.ruta)
    } catch {
      // Si falló la red, el próximo poll lo vuelve a traer — no se pierde.
    } finally {
      setMarcando(false)
    }
  }

  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <div style={st.titulo}>📌 Aviso de {aviso.de?.nombre || 'un compañero'}</div>
        {aviso.referencia && <div style={st.ref}>{aviso.referencia.etiqueta}</div>}
        <div style={st.mensaje}>“{aviso.mensaje}”</div>
        <div style={st.fecha}>{fmtFechaHora(aviso.created_at)}</div>
        {avisos.length > 1 && (
          <div style={st.cola}>Te quedan {avisos.length - 1} aviso{avisos.length > 2 ? 's' : ''} más por leer.</div>
        )}
        <div style={st.acciones}>
          {aviso.referencia?.ruta && (
            <Boton variante="secondary" disabled={marcando} onClick={() => aceptar(true)}>
              Ver {aviso.referencia.etiqueta} →
            </Boton>
          )}
          <Boton variante="primary" disabled={marcando} onClick={() => aceptar(false)}>
            {marcando ? 'Marcando…' : '✓ Leído'}
          </Boton>
        </div>
      </div>
    </div>
  )
}

const st = {
  // Por encima de todo (los modales comunes usan 1000): un aviso sin leer
  // tapa cualquier pantalla hasta que se acepte.
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
  },
  modal: {
    background: '#fff', borderRadius: radius.md, padding: '22px 24px',
    width: 'min(480px, calc(100vw - 32px))', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
    borderTop: `4px solid ${colors.link}`,
  },
  titulo: { fontSize: text.title, fontWeight: 700, color: colors.text },
  ref: {
    marginTop: 6, fontSize: text.sec, color: colors.link, fontWeight: 600,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  mensaje: {
    marginTop: 12, padding: '12px 14px', background: colors.bgSoft,
    borderRadius: radius.sm, fontSize: text.body, color: colors.text, lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  fecha: { marginTop: 8, fontSize: text.meta, color: colors.textSec },
  cola: { marginTop: 8, fontSize: text.sec, color: colors.warn, fontWeight: 600 },
  acciones: { marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' },
}
